import React, { useEffect } from "react";
import { X, Printer } from "lucide-react";
import { refNo } from "../../../software/format";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Generic branded, printable document (work-order invoice / quote).
// brand: {company, tagline, logo_url, phone, email, address, abn, website, colour}
// sections: [{ heading, lines: [{desc, qty, unitPrice, amount}] }]
export default function DocView({ brand = {}, docType, title, number, dateLabel, statusLabel, client, sections = [], subtotal, discount = 0, gstIncl, total, notes, footerNote, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const accent = brand.colour || "#0891B2";
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4 print:p-0 print:bg-white" data-testid="doc-view">
      <style>{`@media print { body * { visibility: hidden !important; } #doc-print, #doc-print * { visibility: visible !important; } #doc-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; } .no-print { display: none !important; } }`}</style>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto print:shadow-none print:max-w-none print:max-h-none">
        <div className="flex justify-between items-center p-3 border-b border-slate-100 no-print sticky top-0 bg-white">
          <span className="font-display font-bold text-slate-900 text-sm">{title} {number ? refNo(docType === "quote" ? "quote" : "invoice", number) : ""}</span>
          <div className="flex gap-2">
            <button onClick={() => window.print()} data-testid="doc-print-btn" className="inline-flex items-center gap-1.5 rounded-full text-white text-xs font-semibold px-3 py-1.5" style={{ background: accent }}><Printer className="h-3.5 w-3.5" /> Print / Save PDF</button>
            <button onClick={onClose} className="rounded-full border border-slate-200 p-1.5 hover:bg-slate-50"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div id="doc-print" className="p-8 text-slate-800">
          {/* letterhead */}
          <div className="flex justify-between items-start gap-4 pb-4 mb-4 border-b-2" style={{ borderColor: accent }}>
            <div className="flex items-center gap-3 min-w-0">
              {brand.logo_url ? <img src={brand.logo_url} alt="" className="h-14 w-14 object-contain" /> : <div className="h-14 w-14 rounded-xl flex items-center justify-center text-white font-display font-bold text-xl" style={{ background: accent }}>{(brand.company || "A")[0]}</div>}
              <div className="min-w-0">
                <p className="font-display font-extrabold text-xl leading-tight" style={{ color: accent }}>{brand.company || "Glamour Pools and Spas"}</p>
                {brand.tagline && <p className="text-xs text-slate-500">{brand.tagline}</p>}
                {brand.abn && <p className="text-[11px] text-slate-400">ABN {brand.abn}</p>}
              </div>
            </div>
            <div className="text-right text-[11px] text-slate-500 shrink-0">
              {brand.address && <p>{brand.address}</p>}
              {brand.phone && <p>{brand.phone}</p>}
              {brand.email && <p>{brand.email}</p>}
              {brand.website && <p>{brand.website}</p>}
            </div>
          </div>

          <div className="flex justify-between items-start mb-5">
            <div>
              <p className="font-display font-bold text-lg text-slate-900">{title}</p>
              {number && <p className="font-mono2 text-sm text-slate-500">{docType === "quote" ? "Quote" : "Invoice"} {refNo(docType === "quote" ? "quote" : "invoice", number)}</p>}
              {statusLabel && <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${accent}22`, color: accent }}>{statusLabel}</span>}
            </div>
            <div className="text-right text-sm">
              <p className="text-slate-400 text-[11px]">{dateLabel || new Date().toLocaleDateString()}</p>
              {client && <div className="mt-1"><p className="font-semibold text-slate-800">{client.name}</p>{client.company && <p className="text-xs text-slate-500">{client.company}</p>}{client.phone && <p className="text-xs text-slate-500">{client.phone}</p>}{client.email && <p className="text-xs text-slate-500">{client.email}</p>}</div>}
            </div>
          </div>

          {sections.map((sec, si) => (
            (sec.lines && sec.lines.length > 0) ? (
              <div key={si} className="mb-4">
                {sec.heading && <p className="font-mono2 text-[10px] uppercase tracking-widest mb-1" style={{ color: accent }}>{sec.heading}</p>}
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200"><th className="text-left py-1 font-medium">Description</th><th className="text-center py-1 font-medium w-16">Qty</th><th className="text-right py-1 font-medium w-24">Unit</th><th className="text-right py-1 font-medium w-24">Amount</th></tr></thead>
                  <tbody>
                    {sec.lines.map((ln, i) => (
                      <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{ln.desc}</td><td className="text-center">{ln.qty}</td><td className="text-right font-mono2">{money(ln.unitPrice)}</td><td className="text-right font-mono2">{money(ln.amount)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null
          ))}

          <div className="flex justify-end mt-4">
            <div className="w-64 text-sm space-y-1">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="font-mono2">{money(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-slate-500"><span>Discount</span><span className="font-mono2">-{money(discount)}</span></div>}
              <div className="flex justify-between font-display font-bold text-lg text-slate-900 border-t border-slate-200 pt-1" style={{ color: accent }}><span>Total</span><span className="font-mono2">{money(total)}</span></div>
              <div className="flex justify-between text-[11px] text-slate-400"><span>Includes GST</span><span className="font-mono2">{money(gstIncl)}</span></div>
            </div>
          </div>

          {notes && <div className="mt-5 text-sm text-slate-600 border-t border-slate-100 pt-3 whitespace-pre-line"><span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 block mb-1">Notes</span>{notes}</div>}
          <p className="text-center text-[11px] text-slate-400 mt-6">{footerNote || `Thank you — ${brand.company || "Aqualux Nexus"}`}</p>
        </div>
      </div>
    </div>
  );
}
